import { useDateBadgedList, useQueryParam, useVirtualList } from '@hooks'
import { ICombinedInvoice, useTags } from '@query'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { InvoiceListItem } from './InvoiceListItem'
import { ITag } from '@root/query/tags'

interface Props {
  combinedInvoices: ICombinedInvoice[]
  onScrollBottom: () => void
  onlyPurchases?: boolean
}

export const InvoiceList: React.FC<Props> = ({
  combinedInvoices,
  onScrollBottom,
  onlyPurchases = false
}) => {
  const [selectedId] = useQueryParam<number>('id', value => parseInt(value, 10))
  const [selectedType] = useQueryParam<string>('type')
  const uniqueTagIds = useMemo(() => {
    return Array.from(new Set(combinedInvoices.flatMap(invoice => invoice.item.tag_ids))).sort(
      (a, b) => a - b
    )
  }, [combinedInvoices])
  const { data: tagsData } = useTags({ tagIds: uniqueTagIds })
  const tagsById = useMemo(() => {
    const map = new Map<number, ITag>()
    for (const tag of tagsData?.results || []) {
      map.set(tag.id, tag)
    }
    return map
  }, [tagsData?.results])

  const badgedList = useDateBadgedList<ICombinedInvoice>({
    items: combinedInvoices,
    itemDateKey: 'item.invoicing_date',
    itemDateGetter: onlyPurchases
      ? invoice => invoice.item.due_date || invoice.item.invoicing_date
      : undefined,
    itemDateFormat: 'YYYY-MM-DD',
    dateFormat: 'MMMM YYYY'
  })

  const { scrollRef, renderItems } = useVirtualList({
    items: badgedList,
    itemSize: 66.5,
    onScrollBottom
  })

  return (
    <InvoiceListScrollable ref={scrollRef}>
      <AnimatePresence>
        {renderItems((item, style) => {
          if (item.type === 'item') {
            return (
              <motion.div
                key={`invoice-${item.item.type}-${item.item.item.id}`}
                layout="position"
                style={style}
              >
                <InvoiceListItem
                  key={`el-${item.type}-${item.item.item.id}`}
                  combinedInvoice={item.item}
                  isSelected={selectedId === item.item.item.id && selectedType === item.item.type}
                  isFirst={item.extra.isFirstOfType}
                  isLast={item.extra.isLastOfType}
                  tagsById={tagsById}
                />
              </motion.div>
            )
          }

          return (
            <DateBadgeWrapper key={item.item.id} style={style}>
              <DateBadge>{item.item.label}</DateBadge>
            </DateBadgeWrapper>
          )
        })}
      </AnimatePresence>
    </InvoiceListScrollable>
  )
}

const InvoiceListScrollable = styled.div`
  overflow: auto;
  flex: 1;
  height: 100%;
`

const DateBadgeWrapper = styled(motion.div).attrs({ layout: 'position' })`
  height: 100%;
  width: 100%;
  flex: 1;
  display: flex;
  justify-content: center;

  & > * {
    align-self: center;
  }
`

const DateBadge = styled.div`
  text-transform: uppercase;
  padding: 0.2rem 0.4rem;
  background: ${({ theme }) => theme.colors.neutralBlack};
  color: ${({ theme }) => theme.colors.metalGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  background: ${({ theme }) => theme.colors.neutralGray};
`
